import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config/Constants';

export interface TorneoConfig {
  modo: 'individual' | 'equipos';
  tamanoEquipo: number;
  participantes: number;
  jugadoresHumanos: number;
  dificultadIA: 'facil' | 'medio' | 'dificil';
  modoJuego: 'clasico' | 'dopamina';
}

export class MenuScene extends Phaser.Scene {
  private config: TorneoConfig = {
    modo: 'individual',
    tamanoEquipo: 2,
    participantes: 4,
    jugadoresHumanos: 2,
    dificultadIA: 'medio',
    modoJuego: 'clasico',
  };

  private menuMode: string = 'rapida';
  private teamSizeLabel!: Phaser.GameObjects.Text;
  private teamSizeToggles: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super({ key: 'MenuScene' });
  }

  init(data: { modo?: string }): void {
    this.menuMode = data.modo || 'rapida';
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#080818');

    // Fondo decorativo
    const bgG = this.add.graphics();
    bgG.lineStyle(1, 0x1a1a4e, 0.2);
    for (let i = 0; i < 15; i++) {
      const y = i * 50;
      bgG.beginPath();
      bgG.moveTo(0, y);
      bgG.lineTo(GAME_WIDTH, y + 30);
      bgG.strokePath();
    }

    // Barra superior
    const titleBar = this.add.graphics();
    titleBar.fillStyle(0x1a1a3e, 0.8);
    titleBar.fillRect(0, 0, GAME_WIDTH, 70);

    const title = this.menuMode === 'torneo' ? '🏆 CONFIGURAR TORNEO' : '⚡ PARTIDA RÁPIDA';
    this.add.text(GAME_WIDTH / 2, 35, title, {
      fontSize: '28px',
      fontFamily: 'Arial Black, Impact, sans-serif',
      color: '#ffffff',
    }).setOrigin(0.5);

    if (this.menuMode === 'torneo') {
      this.createTorneoMenu();
    } else {
      this.createQuickMenu();
    }

    // Botón volver
    const backBtn = this.add.text(60, GAME_HEIGHT - 40, '← VOLVER', {
      fontSize: '18px',
      fontFamily: 'Arial',
      color: '#e74c3c',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => this.scene.start('TitleScene'));
  }

  private createQuickMenu(): void {
    let y = 100;

    // Modo (Individual/Equipos)
    this.add.text(350, y, 'MODO:', { fontSize: '20px', color: '#f1c40f' });
    this.createToggle(600, y, ['Individual', 'Equipos'], 0, (val) => {
      this.config.modo = val === 'Individual' ? 'individual' : 'equipos';
      // Mostrar/ocultar tamaño equipo
      const show = this.config.modo === 'equipos';
      this.teamSizeLabel.setVisible(show);
      this.teamSizeToggles.forEach(t => (t as any).setVisible(show));
    });
    y += 60;

    // Tamaño equipo (solo si equipos)
    this.teamSizeLabel = this.add.text(350, y, 'TAMAÑO EQUIPO:', { fontSize: '20px', color: '#f1c40f' });
    this.teamSizeToggles = this.createToggle(600, y, ['2', '3', '4'], 0, (val) => {
      this.config.tamanoEquipo = parseInt(val);
    });
    // Ocultar por defecto si es individual
    if (this.config.modo === 'individual') {
      this.teamSizeLabel.setVisible(false);
      this.teamSizeToggles.forEach(t => (t as any).setVisible(false));
    }
    y += 60;

    // Participantes
    this.add.text(350, y, 'PARTICIPANTES:', { fontSize: '20px', color: '#f1c40f' });
    this.createToggle(600, y, ['2', '3', '4', '6', '8'], 0, (val) => {
      this.config.participantes = parseInt(val);
    });
    y += 60;

    // Jugadores humanos
    this.add.text(350, y, 'JUGADORES HUMANOS:', { fontSize: '20px', color: '#f1c40f' });
    this.createToggle(600, y, ['1', '2', '3', '4'], this.config.jugadoresHumanos - 1, (val) => {
      this.config.jugadoresHumanos = parseInt(val);
    });
    y += 60;

    // Dificultad IA
    this.add.text(350, y, 'DIFICULTAD IA:', { fontSize: '20px', color: '#f1c40f' });
    this.createToggle(600, y, ['Fácil', 'Medio', 'Difícil'], 1, (val) => {
      this.config.dificultadIA = val.toLowerCase() as any;
    });
    y += 60;

    // Modo juego
    this.add.text(350, y, 'MODO JUEGO:', { fontSize: '20px', color: '#f1c40f' });
    this.createToggle(600, y, ['Clásico', 'Dopamina'], 0, (val) => {
      this.config.modoJuego = val === 'Clásico' ? 'clasico' : 'dopamina';
    });

    y += 80;

    // Botón empezar
    const startBtn = this.add.rectangle(GAME_WIDTH / 2, y, 300, 50, 0x27ae60)
      .setStrokeStyle(2, 0x2ecc71)
      .setInteractive({ useHandCursor: true });
    this.add.text(GAME_WIDTH / 2, y, '🚀 EMPEZAR', {
      fontSize: '22px', color: '#ffffff',
    }).setOrigin(0.5);
    startBtn.on('pointerdown', () => this.startGame());
  }

  private createTorneoMenu(): void {
    let y = 100;

    // Modo torneo
    this.add.text(350, y, 'MODO:', { fontSize: '20px', color: '#f1c40f' });
    this.createToggle(600, y, ['Individual', 'Equipos'], 0, (val) => {
      this.config.modo = val === 'Individual' ? 'individual' : 'equipos';
    });
    y += 60;

    // Tamaño equipo (solo si equipos)
    this.add.text(350, y, 'TAMAÑO EQUIPO:', { fontSize: '20px', color: '#f1c40f' });
    this.createToggle(600, y, ['2', '3', '4'], 0, (val) => {
      this.config.tamanoEquipo = parseInt(val);
    });
    y += 60;

    // Participantes
    this.add.text(350, y, 'PARTICIPANTES:', { fontSize: '20px', color: '#f1c40f' });
    this.createToggle(600, y, ['4', '6', '8', '12', '16'], 2, (val) => {
      this.config.participantes = parseInt(val);
    });
    y += 60;

    // Jugadores humanos
    this.add.text(350, y, 'JUGADORES HUMANOS:', { fontSize: '20px', color: '#f1c40f' });
    this.createToggle(600, y, ['1', '2', '3', '4'], 1, (val) => {
      this.config.jugadoresHumanos = parseInt(val);
    });
    y += 60;

    // Dificultad IA
    this.add.text(350, y, 'DIFICULTAD IA:', { fontSize: '20px', color: '#f1c40f' });
    this.createToggle(600, y, ['Fácil', 'Medio', 'Difícil'], 1, (val) => {
      this.config.dificultadIA = val.toLowerCase() as any;
    });
    y += 60;

    // Modo juego
    this.add.text(350, y, 'MODO JUEGO:', { fontSize: '20px', color: '#f1c40f' });
    this.createToggle(600, y, ['Clásico', 'Dopamina'], 0, (val) => {
      this.config.modoJuego = val === 'Clásico' ? 'clasico' : 'dopamina';
    });

    y += 80;

    // Botón empezar
    const startBtn = this.add.rectangle(GAME_WIDTH / 2, y, 300, 50, 0x27ae60)
      .setStrokeStyle(2, 0x2ecc71)
      .setInteractive({ useHandCursor: true });
    this.add.text(GAME_WIDTH / 2, y, '🏆 INICIAR TORNEO', {
      fontSize: '22px', color: '#ffffff',
    }).setOrigin(0.5);
    startBtn.on('pointerdown', () => this.startGame());
  }

  private createToggle(x: number, y: number, options: string[], defaultIdx: number, onChange: (val: string) => void): Phaser.GameObjects.GameObject[] {
    const btns: Phaser.GameObjects.Rectangle[] = [];
    const labels: Phaser.GameObjects.Text[] = [];
    const all: Phaser.GameObjects.GameObject[] = [];
    let selected = defaultIdx;

    options.forEach((opt, i) => {
      const bx = x + i * 110;
      const isSelected = i === selected;
      const bg = this.add.rectangle(bx, y, 100, 36, isSelected ? 0x3498db : 0x2c3e50)
        .setStrokeStyle(1, 0x3498db)
        .setInteractive({ useHandCursor: true });
      const lbl = this.add.text(bx, y, opt, {
        fontSize: '16px',
        color: isSelected ? '#ffffff' : '#7f8c8d',
      }).setOrigin(0.5);

      btns.push(bg);
      labels.push(lbl);
      all.push(bg, lbl);

      bg.on('pointerdown', () => {
        // Deseleccionar todos
        btns.forEach((b, j) => {
          b.setFillStyle(0x2c3e50);
          labels[j].setColor('#7f8c8d');
        });
        // Seleccionar este
        bg.setFillStyle(0x3498db);
        lbl.setColor('#ffffff');
        selected = i;
        onChange(opt);
      });
    });

    return all;
  }

  private startGame(): void {
    this.scene.start('SelectionScene', {
      config: this.config,
      isTorneo: this.menuMode === 'torneo',
    });
  }
}
